import { Banner, Button, Checkbox, Spinner, TextField } from "@omony/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type ReactNode, type FormEvent } from "react";
import { useIntl } from "react-intl";
import { api, errorMessageId } from "../api.js";
import { clearHash, navigate } from "../hashRoute.js";
import { queryClient, refreshSession } from "../query.js";
import { AuthLayout } from "./AuthLayout.js";
import styles from "./auth.module.css";

type T = (id: string, v?: Record<string, string | number>) => string;
const useT = (): T => {
  const intl = useIntl();
  return (id, v) => intl.formatMessage({ id }, v);
};

export async function logout() {
  await api("/auth/logout", { method: "POST" }).catch(() => undefined);
  queryClient.clear();
  await refreshSession();
}

export function ErrorBanner({ error }: { error: unknown }) {
  const t = useT();
  if (!error) return null;
  return <Banner tone="danger">{t(errorMessageId(error))}</Banner>;
}

/** Passwort-Hinweis nach NIST (Länge statt Sonderzeichen-Regeln) */
function PasswordHint() {
  const t = useT();
  return <p className={styles.lead}>{t("auth.password.hint")}</p>;
}

export function LoginScreen({ appTitle, extra }: { appTitle: string; extra?: ReactNode }) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const m = useMutation({
    mutationFn: () => api<{ next: string }>("/auth/login", { method: "POST", body: { email, password } }),
    onSuccess: () => refreshSession(),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    m.mutate();
  };
  return (
    <AuthLayout appTitle={appTitle} title={t("auth.login.title")} lead={t("auth.login.lead")}>
      <form className={styles.form} onSubmit={submit} noValidate>
        <ErrorBanner error={m.error} />
        <TextField
          label={t("auth.email")}
          type="email"
          autoComplete="username"
          inputMode="email"
          value={email}
          onChange={setEmail}
          isRequired
          // Einziger Zweck der Seite ist die Eingabe – Fokus direkt ins erste Feld (WCAG 3.2.1 unberührt)
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        <TextField
          label={t("auth.password")}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
          isRequired
        />
        <div className={styles.actions}>
          <Button variant="quiet" onPress={() => navigate("passwort-vergessen")}>
            {t("auth.forgot.link")}
          </Button>
          <Button type="submit" isDisabled={m.isPending || !email || !password}>
            {t("auth.login.submit")}
          </Button>
        </div>
      </form>
      {extra}
    </AuthLayout>
  );
}

export function MfaVerifyScreen({ appTitle }: { appTitle: string }) {
  const t = useT();
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const m = useMutation({
    mutationFn: () => api("/auth/mfa", { method: "POST", body: { code } }),
    onSuccess: () => refreshSession(),
    onError: (e) => {
      setCode("");
      if (e instanceof Error && e.message === "mfa_locked") void refreshSession();
    },
  });
  return (
    <AuthLayout
      appTitle={appTitle}
      title={t("auth.mfa.title")}
      lead={t(recovery ? "auth.mfa.recoveryLead" : "auth.mfa.lead")}
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
        noValidate
      >
        <ErrorBanner error={m.error} />
        {recovery ? (
          <TextField
            key="r"
            label={t("auth.mfa.recoveryCode")}
            code
            autoComplete="off"
            value={code}
            onChange={setCode}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        ) : (
          <TextField
            key="c"
            label={t("auth.mfa.code")}
            code
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        )}
        <div className={styles.actions}>
          <Button variant="quiet" onPress={() => setRecovery(!recovery)}>
            {t(recovery ? "auth.mfa.useApp" : "auth.mfa.useRecovery")}
          </Button>
          <Button type="submit" isDisabled={m.isPending || code.length < 6}>
            {t("auth.mfa.submit")}
          </Button>
        </div>
        <Button variant="quiet" onPress={() => void logout()}>
          {t("auth.cancelLogin")}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const t = useT();
  const [saved, setSaved] = useState(false);
  return (
    <div className={styles.form}>
      <Banner tone="warning" live={false}>
        {t("auth.recovery.warning")}
      </Banner>
      <ul className={styles.codes} aria-label={t("auth.recovery.title")}>
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <Checkbox card isSelected={saved} onChange={setSaved}>
        {t("auth.recovery.confirm")}
      </Checkbox>
      <div className={styles.actions}>
        <span />
        <Button isDisabled={!saved} onPress={onDone}>
          {t("common.next")}
        </Button>
      </div>
    </div>
  );
}

export function MfaSetupScreen({ appTitle }: { appTitle: string }) {
  const t = useT();
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const setup = useQuery({
    queryKey: ["mfa-setup"],
    queryFn: () => api<{ secret: string; qrSvg: string }>("/auth/mfa/totp/setup", { method: "POST" }),
    staleTime: Infinity,
    gcTime: 0,
  });
  const confirm = useMutation({
    mutationFn: () =>
      api<{ recoveryCodes: string[] }>("/auth/mfa/totp/confirm", { method: "POST", body: { code } }),
    onSuccess: (r) => setCodes(r.recoveryCodes),
    onError: () => setCode(""),
  });
  if (codes) {
    return (
      <AuthLayout appTitle={appTitle} title={t("auth.recovery.title")} lead={t("auth.recovery.lead")}>
        <RecoveryCodes codes={codes} onDone={() => void refreshSession()} />
      </AuthLayout>
    );
  }
  const secret = setup.data?.secret ?? "";
  return (
    <AuthLayout appTitle={appTitle} title={t("auth.mfaSetup.title")} lead={t("auth.mfaSetup.lead")}>
      {setup.isLoading ? <Spinner label={t("common.loading")} /> : null}
      <ErrorBanner error={setup.error ?? confirm.error} />
      {setup.data ? (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            confirm.mutate();
          }}
          noValidate
        >
          <ol className={styles.steps}>
            <li>{t("auth.mfaSetup.step1")}</li>
            <li>
              <div className={styles.form}>
                <span>{t("auth.mfaSetup.step2")}</span>
                <div className={styles.qrRow}>
                  <img
                    className={styles.qr}
                    src={`data:image/svg+xml;base64,${btoa(setup.data.qrSvg)}`}
                    alt={t("auth.mfaSetup.qrAlt")}
                  />
                  <div>
                    <p className={styles.lead}>{t("auth.mfaSetup.manual")}</p>
                    <p className={styles.secret}>{secret.replace(/(.{4})/g, "$1 ").trim()}</p>
                  </div>
                </div>
              </div>
            </li>
            <li>
              <TextField
                label={t("auth.mfaSetup.step3")}
                code
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))}
              />
            </li>
          </ol>
          <div className={styles.actions}>
            <Button variant="quiet" onPress={() => void logout()}>
              {t("auth.cancelLogin")}
            </Button>
            <Button type="submit" isDisabled={confirm.isPending || code.length !== 6}>
              {t("auth.mfaSetup.submit")}
            </Button>
          </div>
        </form>
      ) : null}
    </AuthLayout>
  );
}

export function NewPasswordFields({
  value,
  onChange,
  repeat,
  onRepeat,
}: {
  value: string;
  onChange: (v: string) => void;
  repeat: string;
  onRepeat: (v: string) => void;
}) {
  const t = useT();
  const mismatch = repeat.length > 0 && repeat !== value;
  return (
    <>
      <TextField
        label={t("auth.password.new")}
        type="password"
        autoComplete="new-password"
        value={value}
        onChange={onChange}
        isRequired
      />
      <TextField
        label={t("auth.password.repeat")}
        type="password"
        autoComplete="new-password"
        value={repeat}
        onChange={onRepeat}
        errorMessage={mismatch ? t("auth.password.mismatch") : undefined}
        isRequired
      />
      <PasswordHint />
    </>
  );
}

export function PasswordChangeScreen({ appTitle, forced }: { appTitle: string; forced: boolean }) {
  const t = useT();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const m = useMutation({
    mutationFn: () => api("/auth/password/change", { method: "POST", body: { current, next } }),
    onSuccess: () => refreshSession(),
  });
  return (
    <AuthLayout
      appTitle={appTitle}
      title={t("auth.passwordChange.title")}
      lead={forced ? t("auth.passwordChange.forced") : undefined}
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
        noValidate
      >
        <ErrorBanner error={m.error} />
        <TextField
          label={t("auth.password.current")}
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={setCurrent}
          isRequired
        />
        <NewPasswordFields value={next} onChange={setNext} repeat={repeat} onRepeat={setRepeat} />
        <div className={styles.actions}>
          <Button variant="quiet" onPress={() => void logout()}>
            {t("auth.logout")}
          </Button>
          <Button type="submit" isDisabled={m.isPending || !current || !next || next !== repeat}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}

export function InvitationScreen({ appTitle, token }: { appTitle: string; token: string }) {
  const t = useT();
  const intl = useIntl();
  const [pw, setPw] = useState("");
  const [repeat, setRepeat] = useState("");
  const info = useQuery({
    queryKey: ["invitation", token],
    queryFn: () =>
      api<{ email: string; role: string; displayName: string; pharmacyName: string | null }>(
        `/auth/invitations/${encodeURIComponent(token)}`,
      ),
    retry: false,
  });
  const accept = useMutation({
    mutationFn: () =>
      api<{ mfaRequired: boolean }>(`/auth/invitations/${encodeURIComponent(token)}/accept`, {
        method: "POST",
        body: { password: pw, uiLanguage: intl.locale },
      }),
  });
  if (accept.isSuccess) {
    return (
      <AuthLayout
        appTitle={appTitle}
        title={t("auth.invitation.doneTitle")}
        lead={t(accept.data.mfaRequired ? "auth.invitation.doneMfa" : "auth.invitation.done")}
      >
        <Button onPress={clearHash}>{t("auth.login.submit")}</Button>
      </AuthLayout>
    );
  }
  if (info.isError) {
    return (
      <AuthLayout appTitle={appTitle} title={t("auth.invitation.title")}>
        <Banner tone="danger">{t("error.token_invalid")}</Banner>
        <Button variant="secondary" onPress={clearHash}>
          {t("nav.home")}
        </Button>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout
      appTitle={appTitle}
      title={t("auth.invitation.title")}
      lead={
        info.data
          ? t("auth.invitation.lead", {
              name: info.data.displayName,
              role: t(`role.${info.data.role}`),
              email: info.data.email,
            })
          : undefined
      }
    >
      {info.isLoading ? <Spinner label={t("common.loading")} /> : null}
      {info.data ? (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            accept.mutate();
          }}
          noValidate
        >
          <ErrorBanner error={accept.error} />
          <input
            type="text"
            name="username"
            autoComplete="username"
            value={info.data.email}
            readOnly
            hidden
          />
          <NewPasswordFields value={pw} onChange={setPw} repeat={repeat} onRepeat={setRepeat} />
          <div className={styles.actions}>
            <span />
            <Button type="submit" isDisabled={accept.isPending || !pw || pw !== repeat}>
              {t("auth.invitation.submit")}
            </Button>
          </div>
        </form>
      ) : null}
    </AuthLayout>
  );
}

export function ForgotScreen({ appTitle }: { appTitle: string }) {
  const t = useT();
  const [email, setEmail] = useState("");
  const m = useMutation({
    mutationFn: () => api("/auth/password/forgot", { method: "POST", body: { email } }),
  });
  return (
    <AuthLayout appTitle={appTitle} title={t("auth.forgot.title")} lead={t("auth.forgot.lead")}>
      {m.isSuccess ? (
        <Banner tone="success">{t("auth.forgot.sent")}</Banner>
      ) : (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            m.mutate();
          }}
          noValidate
        >
          <ErrorBanner error={m.error} />
          <TextField
            label={t("auth.email")}
            type="email"
            autoComplete="username"
            inputMode="email"
            value={email}
            onChange={setEmail}
            isRequired
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <div className={styles.actions}>
            <span />
            <Button type="submit" isDisabled={m.isPending || !email}>
              {t("auth.forgot.submit")}
            </Button>
          </div>
        </form>
      )}
      <Button variant="quiet" onPress={clearHash}>
        {t("auth.backToLogin")}
      </Button>
    </AuthLayout>
  );
}

export function ResetScreen({ appTitle, token }: { appTitle: string; token: string }) {
  const t = useT();
  const [pw, setPw] = useState("");
  const [repeat, setRepeat] = useState("");
  const m = useMutation({
    mutationFn: () => api("/auth/password/reset", { method: "POST", body: { token, password: pw } }),
  });
  return (
    <AuthLayout appTitle={appTitle} title={t("auth.reset.title")}>
      {m.isSuccess ? (
        <>
          <Banner tone="success">{t("auth.reset.done")}</Banner>
          <Button onPress={clearHash}>{t("auth.login.submit")}</Button>
        </>
      ) : (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            m.mutate();
          }}
          noValidate
        >
          <ErrorBanner error={m.error} />
          <NewPasswordFields value={pw} onChange={setPw} repeat={repeat} onRepeat={setRepeat} />
          <div className={styles.actions}>
            <Button variant="quiet" onPress={clearHash}>
              {t("auth.backToLogin")}
            </Button>
            <Button type="submit" isDisabled={m.isPending || !pw || pw !== repeat}>
              {t("common.save")}
            </Button>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}

export function WrongRoleScreen({ appTitle }: { appTitle: string }) {
  const t = useT();
  return (
    <AuthLayout appTitle={appTitle} title={t("auth.wrongRole.title")} lead={t("auth.wrongRole.lead")}>
      <Button onPress={() => void logout()}>{t("auth.logout")}</Button>
    </AuthLayout>
  );
}
